/**
 * Shared helper for Table Explorer handlers — detects a connection-lost result
 * (IPC layer converts thrown errors into `{success:false, error: string}`),
 * queues a pending retry via the banner store, and returns the friendly error
 * message to surface to the user.
 *
 * Extracted as a pure function so it can be unit-tested without rendering
 * the full TableExplorerTab component.
 */
import { usePendingRetryStore } from '../../connection-banner/store/pendingRetryStore';
import { isConnectionLostError } from '../../connection-banner/utils/isConnectionLostError';

export type ConnectionLostRetryOptions = {
  /** The workspace tab id to associate the retry with. */
  tabId: string;
  /** The connection that dropped. */
  connectionId: string;
  /** Banner button label suffix (e.g. 'preview', 'save edits'). */
  label: string;
  /** Callback to replay when the user reconnects. */
  action: () => void;
};

/**
 * If `error` matches a connection-lost pattern, queue a pending retry and
 * return the friendly "use the banner" message; otherwise return `null`
 * (meaning the caller should handle the error normally).
 */
export function checkAndQueueConnectionLostRetry(
  error: unknown,
  opts: ConnectionLostRetryOptions,
): string | null {
  if (!isConnectionLostError(error)) return null;

  usePendingRetryStore.getState().setPendingRetry({
    tabId: opts.tabId,
    requires: [opts.connectionId],
    label: opts.label,
    action: opts.action,
  });

  return "Connection isn't active — use the banner above to reconnect.";
}
